/**
 * OutlineNode - Create an outlined (hollow) shape from a spline
 *
 * Takes a Shape and creates a new Shape with a hole, producing
 * hollow geometry when extruded (like a pipe or frame).
 */

import { BaseNode } from '@nodes/BaseNode';
import { internals, params } from '@/nodes/TypedEndpoints';
import type { NodeDef } from '@runtime/IRuntime';
import type { ThreeDependencies } from '../threejsPlugin';
import type { Three } from '../types';
import type { Payload } from '@/graph/Payload';
import type { ICanvasRenderer } from '@/plugins/video/nodes/VideoCanvasNode';
import { CANVAS_PIXEL_RATIO } from '@/ui/vizConfig';
import { offsetPoints, removeSelfIntersections } from '../polygonUtils';

const outlineEndpoints = {
  ...internals({
    shapeIn: { dataType: 'three:shape', isInput: true, label: 'Shape' },
    shapeOut: { dataType: 'three:shape', isOutput: true }
  }),
  ...params({
    thickness: { value: 0.1, min: 0.01, max: 1, step: 0.01, label: 'Thickness' },
    // Offset direction: true = outward (shape gets bigger), false = inward (hole gets bigger)
    outward: { dataType: 'boolean', value: true, label: 'Expand Outward' }
  })
};

const POINT_RADIUS = 4 * CANVAS_PIXEL_RATIO;

const AUTO_FIT_PADDING = 1.3;
const MIN_SCALE = 1.5;

/** Compute a "nice" grid step that gives roughly 4-8 grid lines per axis. */
function autoGridStep(worldScale: number): number {
  const raw = worldScale / 4;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / mag;
  if (norm < 2) return mag;
  if (norm < 5) return 2 * mag;
  return 5 * mag;
}

function computeWorldScale(points: Array<{ x: number; y: number }> | null, ...extra: Array<Array<{ x: number; y: number }> | null>): number {
  let maxExtent = 0;
  for (const arr of [points, ...extra]) {
    if (!arr) continue;
    for (const p of arr) {
      maxExtent = Math.max(maxExtent, Math.abs(p.x), Math.abs(p.y));
    }
  }
  if (maxExtent === 0) return 2.25;
  return Math.max(maxExtent * AUTO_FIT_PADDING, MIN_SCALE);
}

class OutlineNode extends BaseNode<typeof outlineEndpoints> implements ICanvasRenderer {
  private three: Three;
  private inputShape: any = null;
  private unregisterRenderer: () => void;

  // Canvas renderer state
  private canvas: HTMLCanvasElement | null = null;

  // Cached render data
  private cachedOuterPoints: Array<{ x: number; y: number }> | null = null;
  private cachedInnerPoints: Array<{ x: number; y: number }> | null = null;

  constructor(id: string, deps: ThreeDependencies) {
    super(id, deps);
    this.three = deps.three;
    deps.endpointApi.registerRenderer(this);
    this.unregisterRenderer = deps.endpointApi.unregisterRenderer;
  }

  initialize(): void {
    super.initialize();
  }

  // ─── ICanvasRenderer ──────────────────────────────────────

  setCanvas(canvas: HTMLCanvasElement): void {
    this.canvas = canvas;
    this.render();
  }

  setSize(width: number, height: number): void {
    if (this.canvas) {
      this.canvas.width = width;
      this.canvas.height = height;
      this.render();
    }
  }

  getCanvas(): HTMLCanvasElement | null {
    return this.canvas;
  }

  render(): void {
    if (!this.canvas) return;
    const w = this.canvas.width;
    const h = this.canvas.height;
    if (!w || !h) return;

    const ctx = this.canvas.getContext('2d');
    if (!ctx) return;

    // Clear
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(0, 0, w, h);

    // Auto-fit scale from points
    const worldScale = computeWorldScale(this.cachedOuterPoints, this.cachedInnerPoints);
    const gridStepWorld = autoGridStep(worldScale);

    // Draw grid
    ctx.strokeStyle = '#333';
    ctx.lineWidth = 1;
    const drawScale = Math.min(w, h) / (worldScale * 2);
    const gridStep = gridStepWorld * drawScale;

    for (let x = w / 2 % gridStep; x < w; x += gridStep) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, h);
      ctx.stroke();
    }
    for (let y = h / 2 % gridStep; y < h; y += gridStep) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(w, y);
      ctx.stroke();
    }

    // Draw axes
    ctx.strokeStyle = '#555';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, h / 2);
    ctx.lineTo(w, h / 2);
    ctx.moveTo(w / 2, 0);
    ctx.lineTo(w / 2, h);
    ctx.stroke();

    const outerPoints = this.cachedOuterPoints;
    const innerPoints = this.cachedInnerPoints;

    if (!outerPoints || outerPoints.length === 0) {
      ctx.fillStyle = '#666';
      ctx.font = `${12 * CANVAS_PIXEL_RATIO}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('No shape connected', w / 2, h / 2);
      return;
    }

    // World-to-canvas transform
    const s = Math.min(w, h) / (worldScale * 2);
    const toCanvas = (wx: number, wy: number): [number, number] => [
      w / 2 + wx * s,
      h / 2 - wy * s
    ];

    // Draw outer shape fill (with hole cutout)
    if (outerPoints.length >= 3) {
      ctx.fillStyle = 'rgba(59, 130, 246, 0.15)';
      ctx.beginPath();
      const [sx, sy] = toCanvas(outerPoints[0].x, outerPoints[0].y);
      ctx.moveTo(sx, sy);
      for (let i = 1; i < outerPoints.length; i++) {
        const [px, py] = toCanvas(outerPoints[i].x, outerPoints[i].y);
        ctx.lineTo(px, py);
      }
      ctx.closePath();

      // Cut out hole
      if (innerPoints && innerPoints.length >= 3) {
        const [hx, hy] = toCanvas(innerPoints[0].x, innerPoints[0].y);
        ctx.moveTo(hx, hy);
        for (let i = 1; i < innerPoints.length; i++) {
          const [px, py] = toCanvas(innerPoints[i].x, innerPoints[i].y);
          ctx.lineTo(px, py);
        }
        ctx.closePath();
      }
      ctx.fill('evenodd');
    }

    // Draw outer path
    if (outerPoints.length >= 2) {
      ctx.strokeStyle = '#3b82f6';
      ctx.lineWidth = 2;
      ctx.beginPath();
      const [sx, sy] = toCanvas(outerPoints[0].x, outerPoints[0].y);
      ctx.moveTo(sx, sy);
      for (let i = 1; i < outerPoints.length; i++) {
        const [px, py] = toCanvas(outerPoints[i].x, outerPoints[i].y);
        ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.stroke();

      // Draw outer points
      ctx.fillStyle = '#3b82f6';
      for (const pt of outerPoints) {
        const [px, py] = toCanvas(pt.x, pt.y);
        ctx.beginPath();
        ctx.arc(px, py, POINT_RADIUS, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Draw hole path
    if (innerPoints && innerPoints.length >= 2) {
      ctx.strokeStyle = '#f59e0b';
      ctx.lineWidth = 2;
      ctx.beginPath();
      const [sx, sy] = toCanvas(innerPoints[0].x, innerPoints[0].y);
      ctx.moveTo(sx, sy);
      for (let i = 1; i < innerPoints.length; i++) {
        const [px, py] = toCanvas(innerPoints[i].x, innerPoints[i].y);
        ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.stroke();

      // Draw hole points
      ctx.fillStyle = '#f59e0b';
      for (const pt of innerPoints) {
        const [px, py] = toCanvas(pt.x, pt.y);
        ctx.beginPath();
        ctx.arc(px, py, POINT_RADIUS, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  onInternalChanged(payload: Payload, endpointId: string): void {
    if (endpointId === 'shapeIn') {
      this.inputShape = payload?.value;
      this.generateOutline();
    }
  }

  onParamChanged(): void {
    this.generateOutline();
  }

  private generateOutline(): void {
    if (!this.inputShape || !this.three) {
      this.cachedOuterPoints = null;
      this.cachedInnerPoints = null;
      this.internals.shapeOut.set(null);
      this.render();
      return;
    }

    const thickness = this.params.thickness.value;
    const outward = this.params.outward.value;

    // Get points from input shape
    const inputPoints = this.inputShape.getPoints(64);
    if (inputPoints.length < 3) {
      this.cachedOuterPoints = null;
      this.cachedInnerPoints = null;
      this.internals.shapeOut.set(null);
      this.render();
      return;
    }

    // Convert to simple {x, y} objects
    let points = inputPoints.map((p: any) => ({ x: p.x, y: p.y }));

    // Remove duplicate closing point if first and last are the same
    // (getPoints on closed shapes often returns the start point twice)
    if (points.length > 3) {
      const first = points[0];
      const last = points[points.length - 1];
      const dist = Math.sqrt((first.x - last.x) ** 2 + (first.y - last.y) ** 2);
      if (dist < 0.0001) {
        points = points.slice(0, -1);
      }
    }

    // Create outer and inner paths based on direction
    const halfThickness = thickness / 2;
    const outerPoints = outward
      ? removeSelfIntersections(offsetPoints(points, halfThickness))
      : [...points]; // Don't modify original
    const innerPoints = outward
      ? [...points] // Don't modify original
      : removeSelfIntersections(offsetPoints(points, -halfThickness));

    // Cache for rendering
    this.cachedOuterPoints = outerPoints;
    this.cachedInnerPoints = innerPoints;

    // Create new shape from outer path
    const shape = new this.three.Shape();
    shape.moveTo(outerPoints[0].x, outerPoints[0].y);
    for (let i = 1; i < outerPoints.length; i++) {
      shape.lineTo(outerPoints[i].x, outerPoints[i].y);
    }
    shape.closePath();

    // Create hole from inner path (same winding as outer - Three.js handles subtraction)
    const hole = new this.three.Path();
    hole.moveTo(innerPoints[0].x, innerPoints[0].y);
    for (let i = 1; i < innerPoints.length; i++) {
      hole.lineTo(innerPoints[i].x, innerPoints[i].y);
    }
    hole.closePath();

    shape.holes.push(hole);
    this.internals.shapeOut.set(shape);
    this.render();
  }

  dispose(): void {
    this.unregisterRenderer();
    this.canvas = null;
    super.dispose();
  }
}

export const outlineNodeDef: NodeDef<OutlineNode> = {
  type: 'Outline',
  shortName: 'outline',
  category: 'shape',
  info: 'Creates a hollow shape by adding an inner hole.\nConnect to Extrude to make tubes/frames.',
  endpoints: outlineEndpoints,
  ctor: OutlineNode
};
